import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Plus, PencilSimple, Trash } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useConfirm } from '../components/ConfirmProvider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STREET_TYPES = ['', 'Rue', 'Avenue', 'Boulevard', 'Chemin', 'Route', 'Impasse', 'Place', 'Allée', 'Lotissement', 'Cité', 'Ruelle', 'Quai', 'Cours'];

const emptyForm = {
  name: '',
  email: '',
  phone: '',
  street_number: '',
  street_type: '',
  street_name: '',
  postal_code: '',
  city: '',
  address: '',
};

const Clients = () => {
  const confirm = useConfirm();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState(emptyForm);
  const [postalSuggestions, setPostalSuggestions] = useState([]);

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients`, { withCredentials: true });
      setClients(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (editingClient) {
        await axios.put(`${API_URL}/api/clients/${editingClient.id}`, payload, { withCredentials: true });
        toast.success('Client mis à jour');
      } else {
        await axios.post(`${API_URL}/api/clients`, payload, { withCredentials: true });
        toast.success('Client créé');
      }
      setShowDialog(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm('Êtes-vous sûr de vouloir supprimer ce client ?'))) return;
    try {
      await axios.delete(`${API_URL}/api/clients/${id}`, { withCredentials: true });
      toast.success('Client supprimé');
      fetchClients();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingClient(null);
    setPostalSuggestions([]);
  };

  const openEditDialog = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      street_number: client.street_number || '',
      street_type: client.street_type || '',
      street_name: client.street_name || '',
      postal_code: client.postal_code || '',
      city: client.city || '',
      address: client.address || '',
    });
    setShowDialog(true);
  };

  // Postal code lookup
  const onPostalChange = async (value) => {
    setFormData({ ...formData, postal_code: value });
    if (value.length >= 2) {
      try {
        const r = await axios.get(`${API_URL}/api/postal-codes?q=${encodeURIComponent(value)}`, { withCredentials: true });
        setPostalSuggestions(r.data || []);
      } catch (e) {
        setPostalSuggestions([]);
      }
    } else {
      setPostalSuggestions([]);
    }
  };

  const pickPostalSuggestion = (s) => {
    setFormData({ ...formData, postal_code: s.postal_code, city: s.label });
    setPostalSuggestions([]);
  };

  const onCityChange = async (value) => {
    setFormData({ ...formData, city: value });
    if (value.length >= 2) {
      try {
        const r = await axios.get(`${API_URL}/api/postal-codes?q=${encodeURIComponent(value)}`, { withCredentials: true });
        setPostalSuggestions(r.data || []);
      } catch (e) {
        setPostalSuggestions([]);
      }
    } else {
      setPostalSuggestions([]);
    }
  };

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="clients-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Clients</h1>
            <p className="text-slate-600">Gérez votre base de clients</p>
          </div>
          <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="create-client-btn" className="bg-orange-500 hover:bg-orange-400 text-white">
            <Plus size={20} weight="bold" className="mr-2" /> Créer un client
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">Chargement...</div>
        ) : (
          <>
            <div className="mb-4">
              <input
                type="text"
                placeholder="Rechercher par nom, email, téléphone ou ville..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-clients-input"
                className="w-full md:w-96 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Nom</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Email</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Téléphone</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Adresse</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">CP / Ville</th>
                      <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients
                      .filter(c => !searchQuery ||
                        (c.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                        (c.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                        (c.phone || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                        (c.city || '').toLowerCase().includes(searchQuery.toLowerCase())
                      )
                      .map((client) => (
                      <tr key={client.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 text-sm text-slate-900 font-medium">{client.name}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{client.email || '-'}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{client.phone || '-'}</td>
                        <td className="py-3 px-4 text-sm text-slate-600 whitespace-pre-line">{client.address || '-'}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{client.postal_code} {client.city}</td>
                        <td className="py-3 px-4 text-right">
                          <button onClick={() => openEditDialog(client)} data-testid={`edit-client-${client.id}`} className="inline-flex items-center justify-center p-2 text-blue-600 hover:bg-blue-50 rounded-md mr-2">
                            <PencilSimple size={18} />
                          </button>
                          <button onClick={() => handleDelete(client.id)} data-testid={`delete-client-${client.id}`} className="inline-flex items-center justify-center p-2 text-red-600 hover:bg-red-50 rounded-md">
                            <Trash size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {clients.length === 0 && (
                      <tr><td colSpan="6" className="py-12 text-center text-slate-500">Aucun client enregistré</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent data-testid="client-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingClient ? 'Modifier' : 'Créer'} un client</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nom *</label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="client-name-input" required />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="client-email-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone</label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="client-phone-input" />
                </div>
              </div>
              <div className="border-t border-slate-200 pt-3">
                <h3 className="text-sm font-semibold text-slate-800 mb-3">Adresse</h3>
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-3">
                    <label className="block text-xs font-medium text-slate-600 mb-1">N°</label>
                    <Input value={formData.street_number} onChange={(e) => setFormData({ ...formData, street_number: e.target.value })} data-testid="client-street-number-input" placeholder="12 bis" />
                  </div>
                  <div className="col-span-4">
                    <label className="block text-xs font-medium text-slate-600 mb-1">Type de voie</label>
                    <select value={formData.street_type} onChange={(e) => setFormData({ ...formData, street_type: e.target.value })} data-testid="client-street-type-input" className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white">
                      {STREET_TYPES.map(t => <option key={t} value={t}>{t || '—'}</option>)}
                    </select>
                  </div>
                  <div className="col-span-5">
                    <label className="block text-xs font-medium text-slate-600 mb-1">Intitulé de la voie</label>
                    <Input value={formData.street_name} onChange={(e) => setFormData({ ...formData, street_name: e.target.value })} data-testid="client-street-name-input" placeholder="des Lilas" />
                  </div>
                  <div className="col-span-4 relative">
                    <label className="block text-xs font-medium text-slate-600 mb-1">Code postal</label>
                    <Input value={formData.postal_code} onChange={(e) => onPostalChange(e.target.value)} data-testid="client-postal-input" placeholder="97400" />
                  </div>
                  <div className="col-span-8 relative">
                    <label className="block text-xs font-medium text-slate-600 mb-1">Ville / Commune (lieu-dit)</label>
                    <Input value={formData.city} onChange={(e) => onCityChange(e.target.value)} data-testid="client-city-input" placeholder="ST DENIS (VILLE)" />
                    {postalSuggestions.length > 0 && (
                      <div data-testid="postal-suggestions" className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {postalSuggestions.slice(0, 25).map((s, i) => (
                          <button
                            type="button"
                            key={`${s.postal_code}-${s.label}-${i}`}
                            onClick={() => pickPostalSuggestion(s)}
                            data-testid={`postal-suggestion-${i}`}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-100 text-sm flex justify-between"
                          >
                            <span className="font-medium">{s.label}</span>
                            <span className="text-slate-500">{s.postal_code}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-2">L&apos;adresse complète sera générée automatiquement à partir de ces champs.</p>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)} data-testid="cancel-client-btn">Annuler</Button>
                <Button type="submit" data-testid="submit-client-btn" className="bg-blue-600 hover:bg-blue-500 text-white">
                  {editingClient ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Clients;
